import React, { useState } from 'react';
import { Button } from '@/Components/ui/button';
import { CheckCircle2, Upload, Loader2, X } from 'lucide-react';

const UploadPopup = ({ jobId, onClose, onUploadSuccess }) => {
  const [selectedFile, setSelectedFile] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    stage: 'preparing',
    message: 'Preparing to upload...'
  });
  const [duplicates, setDuplicates] = useState([]);

  const progressMessages = {
    preparing: 'Preparing files for upload...',
    uploading: 'Uploading resumes to server...',
    scoring: 'Calculating match scores...',
    finalizing: 'Finalizing results...'
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFile(files);
    setError('');
    setDuplicates([]);
  };

  const updateProgress = (stage) => {
    setUploadProgress({ stage, message: progressMessages[stage] });
  };

  const handleUpload = async () => {
    if (selectedFile.length === 0) {
      setError('Please select at least one PDF file');
      return;
    }
    setIsUploading(true);
    updateProgress('preparing');
    try {
      const formData = new FormData();
      selectedFile.forEach((file, index) => formData.append(`file${index}`, file));
      formData.append('job_id', jobId);

      updateProgress('uploading');
      const response = await fetch('/api/handle_resume_upload', { method: 'POST', body: formData });

      if (!response.ok) {
        const errText = await response.text().catch(() => 'File upload failed.');
        throw new Error(errText || 'File upload failed.');
      }

      updateProgress('scoring');
      await new Promise(resolve => setTimeout(resolve, 500));
      updateProgress('finalizing');
      await new Promise(resolve => setTimeout(resolve, 500));

      const result = await response.json();
      const dupList = result.duplicates || [];
      setDuplicates(dupList);
      onUploadSuccess(result.newCandidates || []);
      setShowSuccess(true);

      if (!dupList.length) {
        setTimeout(() => onClose(), 3000);
      }
    } catch (err) {
      setError('An error occurred while uploading the file');
      console.error('Error uploading files:', err);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      {!showSuccess && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-indigo-600" />
                <h2 className="text-lg font-semibold text-slate-800">Upload Resumes</h2>
              </div>
              <button onClick={onClose} disabled={isUploading} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500 disabled:opacity-40">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <p className="text-sm text-slate-500">Please select PDF, DOC, DOCX, or TXT files to upload.</p>

              {isUploading ? (
                <div className="flex flex-col items-center gap-4 py-6">
                  <div className="flex gap-2">
                    {['0ms', '150ms', '300ms'].map((delay, i) => (
                      <div key={i} className="w-3 h-3 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: delay }} />
                    ))}
                  </div>
                  <p className="text-sm text-slate-600 font-medium">{uploadProgress.message}</p>
                  <div className="flex flex-col gap-1 w-full">
                    {Object.keys(progressMessages).map((stage) => (
                      <div
                        key={stage}
                        className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${
                          uploadProgress.stage === stage
                            ? 'bg-indigo-100 text-indigo-700 font-semibold'
                            : 'text-slate-400'
                        }`}
                      >
                        {progressMessages[stage]}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <input
                    type="file"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.txt"
                    multiple
                  />
                  {error && <p className="text-sm text-red-600">{error}</p>}

                  {selectedFile.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700 mb-2">Selected Files:</h3>
                      <ul className="text-sm text-slate-600 list-disc list-inside space-y-1">
                        {selectedFile.map((file, i) => <li key={i}>{file.name}</li>)}
                      </ul>
                    </div>
                  )}

                  {duplicates.length > 0 && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <h4 className="text-sm font-semibold text-yellow-800 mb-2">Previously detected duplicates:</h4>
                      <ul className="text-sm text-yellow-700 space-y-1">
                        {duplicates.map((d, i) => (
                          <li key={i}>
                            <strong>{d.uploaded_filename || 'unknown'}</strong>
                            {d.reasons?.length > 0 && <span> — {d.reasons.join(', ')}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex justify-end gap-2 px-5 pb-5">
              <Button variant="outline" onClick={onClose} disabled={isUploading}>Cancel</Button>
              <Button
                onClick={handleUpload}
                disabled={isUploading || selectedFile.length === 0}
                className="gap-2"
              >
                {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {isUploading ? 'Uploading...' : 'Upload CV'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showSuccess && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
            <div className="flex flex-col items-center gap-4 text-center">
              <CheckCircle2 className="w-16 h-16 text-green-500" />
              <h2 className="text-xl font-bold text-slate-800">Success!</h2>
              <p className="text-slate-500">Resumes processed successfully</p>
              <p className="text-sm text-slate-400">{selectedFile.length} resume(s) analyzed</p>
            </div>

            {duplicates.length > 0 && (
              <div className="mt-5 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                <h3 className="text-sm font-semibold text-yellow-800 mb-2">Duplicate File(s) Found</h3>
                <p className="text-xs text-yellow-700 mb-3">The following files were not uploaded (already exist):</p>
                <ul className="space-y-1">
                  {duplicates.map((d, i) => (
                    <li key={i} className="text-sm font-medium text-yellow-800">
                      {d.uploaded_filename || 'unknown'}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-center mt-5">
              <Button onClick={onClose}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UploadPopup;
